import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface UIState {
  sidebarOpen: boolean
  sidebarFullscreen: boolean
  searchOpen: boolean
  /** 侧边栏切换整页搜索时，带过来的待执行搜索关键字（弹窗读取后清空） */
  pendingSearchQuery: string
  settingsOpen: boolean
  blockReferencePanelOpen: boolean
  tocOutlineOpen: boolean
  sidebarSearchOpen: boolean
  currentView: 'editor' | 'preview' | 'split'
  theme: 'light' | 'dark' | 'auto'
  layout: {
    sidebarWidth: number
    editorWidth: number
    panelWidth: number
  }
  notifications: Array<{
    id: string
    type: 'success' | 'error' | 'warning' | 'info'
    title: string
    message: string
    timestamp: number
    duration?: number
  }>
  modals: {
    createNote: boolean
    deleteNote: boolean
    settings: boolean
    export: boolean
    import: boolean
  }
}

const initialState: UIState = {
  sidebarOpen: true,
  sidebarFullscreen: false,
  searchOpen: false,
  pendingSearchQuery: '',
  settingsOpen: false,
  blockReferencePanelOpen: false,
  tocOutlineOpen: false,
  sidebarSearchOpen: false,
  currentView: 'editor',
  theme: 'auto',
  layout: {
    sidebarWidth: 320,
    editorWidth: 600,
    panelWidth: 280
  },
  notifications: [],
  modals: {
    createNote: false,
    deleteNote: false,
    settings: false,
    export: false,
    import: false
  }
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen
    },
    
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload
    },

    toggleSidebarFullscreen: (state) => {
      state.sidebarFullscreen = !state.sidebarFullscreen
    },

    setSidebarFullscreen: (state, action: PayloadAction<boolean>) => {
      state.sidebarFullscreen = action.payload
    },
    
    toggleSearch: (state) => {
      state.searchOpen = !state.searchOpen
    },
    
    setSearchOpen: (state, action: PayloadAction<boolean>) => {
      state.searchOpen = action.payload
    },

    setPendingSearchQuery: (state, action: PayloadAction<string>) => {
      state.pendingSearchQuery = action.payload
    },
    
    toggleSettings: (state) => {
      state.settingsOpen = !state.settingsOpen
    },
    
    setSettingsOpen: (state, action: PayloadAction<boolean>) => {
      state.settingsOpen = action.payload
    },
    
    toggleBlockReferencePanel: (state) => {
      state.blockReferencePanelOpen = !state.blockReferencePanelOpen
    },

    setBlockReferencePanelOpen: (state, action: PayloadAction<boolean>) => {
      state.blockReferencePanelOpen = action.payload
    },

    toggleTocOutline: (state) => {
      state.tocOutlineOpen = !state.tocOutlineOpen
    },
    
    setTocOutlineOpen: (state, action: PayloadAction<boolean>) => {
      state.tocOutlineOpen = action.payload
    },
    
    toggleSidebarSearch: (state) => {
      state.sidebarSearchOpen = !state.sidebarSearchOpen
    },

    setSidebarSearchOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarSearchOpen = action.payload
    },
    
    setCurrentView: (state, action: PayloadAction<UIState['currentView']>) => {
      state.currentView = action.payload
    },
    
    setTheme: (state, action: PayloadAction<UIState['theme']>) => {
      state.theme = action.payload
      // Apply theme to document
      if (typeof document !== 'undefined') {
        const root = document.documentElement
        if (action.payload === 'dark' || (action.payload === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
          root.classList.add('dark')
        } else {
          root.classList.remove('dark')
        }
      }
    },
    
    setLayout: (state, action: PayloadAction<Partial<UIState['layout']>>) => {
      state.layout = { ...state.layout, ...action.payload }
    },
    
    addNotification: (state, action: PayloadAction<Omit<UIState['notifications'][0], 'id' | 'timestamp'>>) => {
      const notification = {
        ...action.payload,
        id: Math.random().toString(36).substr(2, 9),
        timestamp: Date.now()
      }
      state.notifications.push(notification)
    },
    
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload)
    },
    
    clearNotifications: (state) => {
      state.notifications = []
    },
    
    setModalOpen: (state, action: PayloadAction<{ modal: keyof UIState['modals']; open: boolean }>) => {
      state.modals[action.payload.modal] = action.payload.open
    },
    
    closeAllModals: (state) => {
      Object.keys(state.modals).forEach(key => {
        state.modals[key as keyof UIState['modals']] = false
      })
    }
  }
})

export const {
  toggleSidebar,
  setSidebarOpen,
  toggleSidebarFullscreen,
  setSidebarFullscreen,
  toggleSearch,
  setSearchOpen,
  setPendingSearchQuery,
  toggleSettings,
  setSettingsOpen,
  toggleBlockReferencePanel,
  setBlockReferencePanelOpen,
  toggleTocOutline,
  setTocOutlineOpen,
  toggleSidebarSearch,
  setSidebarSearchOpen,
  setCurrentView,
  setTheme,
  setLayout,
  addNotification,
  removeNotification,
  clearNotifications,
  setModalOpen,
  closeAllModals
} = uiSlice.actions

export default uiSlice.reducer